import { clsx } from 'clsx';
import type { GlobalDiscountControlProps } from './MasterDocument.types';
import { useMasterDocumentTranslation } from './i18n';

/**
 * Control de descuento global a nivel de documento.
 *
 * Permite al usuario aplicar un descuento en modo porcentaje (%) o monto fijo
 * ($). Muestra el equivalente calculado en la unidad complementaria (si el modo
 * es porcentaje, calcula el monto; si es monto, calcula el porcentaje). En modo
 * de solo lectura (`editable={false}`) suprime los controles y presenta el
 * valor formateado.
 *
 * @see docs/colors.md - Sistema de tokens de color
 * @see MasterDocument.types - GlobalDiscountControlProps
 *
 * @example
 * ```tsx
 * <GlobalDiscountControl
 *   data={{ mode: 'percentage', percentage: 5 }}
 *   onChange={(d) => setDiscount(d)}
 *   subtotal={1200000}
 *   formatCurrency={formatCurrency}   // de useMasterDocumentFormatters()
 * />
 * ```
 */
export function GlobalDiscountControl({
  data,
  onChange,
  subtotal,
  formatCurrency,
  editable = true,
  className,
}: GlobalDiscountControlProps) {
  const { t } = useMasterDocumentTranslation();
  const isPercentage = data.mode === 'percentage';

  const computedAmount = isPercentage
    ? subtotal * ((data.percentage ?? 0) / 100)
    : (data.amount ?? 0);

  const computedPct = !isPercentage && subtotal > 0
    ? ((data.amount ?? 0) / subtotal) * 100
    : (data.percentage ?? 0);

  return (
    <div
      className={clsx(
        'flex items-center gap-3 px-4 py-2.5 rounded-xl',
        'bg-bg-primary shadow-sm',
        'dark:bg-dark-bg-primary dark:border dark:border-dark-border-primary',
        className,
      )}
    >
      {/* Label */}
      <span className="text-xs font-semibold text-content-primary dark:text-dark-content-primary shrink-0">
        {t('master_document.discount.globalLabel')}
      </span>

      {/* Mode toggle */}
      {editable && (
        <div className="flex rounded-md border border-border-primary dark:border-dark-border-primary overflow-hidden shrink-0">
          <button
            type="button"
            onClick={() => onChange({ mode: 'percentage', percentage: computedPct, amount: undefined })}
            aria-label={t('master_document.discount.applyPercent')}
            aria-pressed={isPercentage}
            className={clsx(
              'px-2.5 py-1 text-xs font-semibold transition-colors',
              isPercentage
                ? 'bg-primary-600 text-white dark:text-dark-content-inverse dark:bg-primary-500'
                : 'bg-transparent text-content-secondary dark:text-dark-content-secondary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary',
            )}
          >
            %
          </button>
          <button
            type="button"
            onClick={() => onChange({ mode: 'amount', percentage: undefined, amount: computedAmount })}
            aria-label={t('master_document.discount.applyAmount')}
            aria-pressed={!isPercentage}
            className={clsx(
              'px-2.5 py-1 text-xs font-semibold transition-colors',
              !isPercentage
                ? 'bg-primary-600 text-white dark:text-dark-content-inverse dark:bg-primary-500'
                : 'bg-transparent text-content-secondary dark:text-dark-content-secondary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary',
            )}
          >
            $
          </button>
        </div>
      )}

      {/* Input */}
      {editable ? (
        <div className="relative flex-1 max-w-[160px]">
          <input
            type="number"
            step={isPercentage ? '0.01' : '1'}
            min="0"
            max={isPercentage ? '100' : subtotal}
            value={isPercentage ? (data.percentage ?? '') : (data.amount ?? '')}
            aria-label={t('master_document.discount.valueAria')}
            onChange={(e) => {
              const val = e.target.value === '' ? undefined : Number(e.target.value);
              if (isPercentage) {
                onChange({ mode: 'percentage', percentage: val, amount: undefined });
              } else {
                onChange({ mode: 'amount', percentage: undefined, amount: val });
              }
            }}
            className={clsx(
              "w-full text-xs tabular-nums font-semibold text-right",
              'px-2.5 py-1.5 rounded-md',
              'border border-border-primary dark:border-dark-border-primary',
              'bg-bg-primary dark:bg-dark-bg-secondary',
              'text-content-primary dark:text-dark-content-primary',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
              isPercentage ? 'pr-6' : 'pl-5',
            )}
          />
          {isPercentage ? (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-content-tertiary dark:text-dark-content-secondary">
              %
            </span>
          ) : (
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-content-tertiary dark:text-dark-content-secondary">
              $
            </span>
          )}
        </div>
      ) : (
        <span className="text-xs tabular-nums font-semibold text-content-primary dark:text-dark-content-primary">
          {isPercentage ? `${data.percentage ?? 0}%` : formatCurrency(data.amount ?? 0)}
        </span>
      )}

      {/* Computed equivalent */}
      <span className="text-xs text-content-tertiary dark:text-dark-content-secondary shrink-0 ml-auto">
        {isPercentage
          ? formatCurrency(computedAmount)
          : `${subtotal > 0 ? computedPct.toFixed(2) : '0.00'}%`
        }
      </span>
    </div>
  );
}
